import React, { useState, useEffect, useCallback } from "react";
import SearchableDropdown from "../../../ui/searchabledropdown";
import "./Form.css";
import { useNavigate } from "react-router-dom";
import {
  formatDateTime,
  getColumn,
  toISTInputValue,
} from "../../../../utils/fn";
import { areObjectsEqual } from "../../../site/fn";
import { IoTrash, IoRefresh } from "react-icons/io5";
import {
  getNodeServiceBaseUrl,
  getSwaggerServiceBaseUrl,
  getAssetServiceBaseUrl,
} from "../../../../config/apiBase";

const mapCustomersForDropdown = (customersData) => {
  if (!Array.isArray(customersData)) return [];
  return customersData.map((customer) => ({
    id: customer.id,
    value: customer.name || customer.value,
  }));
};

const mapProjectsForDropdown = (projects) => {
  if (!Array.isArray(projects)) return [];
  return projects
    .filter((project) => project && (project.projectId ?? project.id) != null)
    .map((project) => ({
      id: project.projectId ?? project.id,
      value: project.projectName ?? project.name ?? `Project ${project.projectId ?? project.id}`,
    }));
};

const fetchProjectsForCustomer = async (customerId, token) => {
  if (customerId == null || customerId === "") return [];
  const cid = String(customerId);
  const headers = { Authorization: `Bearer ${token}` };
  const base = getSwaggerServiceBaseUrl();

  try {
    const response = await fetch(
      `${base}/api/get-projects-by-customer-id?customerId=${encodeURIComponent(cid)}`,
      { headers }
    );
    if (response.ok) {
      const data = await response.json();
      const mapped = mapProjectsForDropdown(data.projects || []);
      if (mapped.length > 0) return mapped;
    } else {
      console.warn(
        "get-projects-by-customer-id failed:",
        response.status,
        await response.text().catch(() => "")
      );
    }
  } catch (error) {
    console.error("Error fetching projects for customer:", error);
  }

  // Fallback: load all projects and filter by customer (covers 404 / partial API failures)
  try {
    const allRes = await fetch(`${base}/api/get-project`, { headers });
    if (!allRes.ok) return [];
    const allData = await allRes.json();
    const filtered = (allData.projects || []).filter(
      (p) => String(p.customerId ?? p.customer_id ?? "") === cid
    );
    return mapProjectsForDropdown(filtered);
  } catch (error) {
    console.error("Fallback project fetch failed:", error);
    return [];
  }
};

const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func.apply(this, args);
    }, delay);
  };
};

/** Other-type assets (Manpower, Diesel, etc.) are not exclusive equipment. */
const isExemptFromAssetDateConflict = (asset) => {
  if (!asset) return false;
  return String(asset.asset_type || "").trim().toLowerCase() === "other";
};

// Helper function to convert ISO date to DD/MM/YYYY format for display
const formatDateToDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Helper function to convert ISO datetime to DD/MM/YYYY HH:MM format for display
const formatDateTimeToDDMMYYYY = (isoDate) => {
  if (!isoDate) return "";
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

// Helper function to convert DD/MM/YYYY to YYYY-MM-DD for input[type="date"]
const convertDDMMYYYYToInputDate = (ddmmyyyy) => {
  if (!ddmmyyyy) return "";
  const parts = ddmmyyyy.split("/");
  if (parts.length !== 3) return "";
  return `${parts[2]}-${parts[1]}-${parts[0]}`;
};

// Helper function to convert YYYY-MM-DD from input to DD/MM/YYYY for storage
const convertInputDateToDDMMYYYY = (yyyymmdd) => {
  if (!yyyymmdd) return null;
  const parts = yyyymmdd.split("-");
  if (parts.length !== 3) return null;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

// Fix 1: Helper function to check if there are actual new files to upload
const hasNewFilesToUpload = (recordFiles) => {
  if (!recordFiles || Object.keys(recordFiles).length === 0) {
    return false;
  }
  
  for (const fileField in recordFiles) {
    const files = recordFiles[fileField];
    
    // Check if files is a FileList or Array with actual File objects
    if (files instanceof FileList && files.length > 0) {
      return true;
    }
    if (Array.isArray(files)) {
      // Filter out any existing file objects that have 'file_url' (already uploaded)
      const newFiles = files.filter(f => f instanceof File && !f.file_url);
      if (newFiles.length > 0) {
        return true;
      }
    }
    if (files instanceof File) {
      return true;
    }
  }
  
  return false;
};

const WorkOrderForm = ({ workOrderId: propWorkOrderId, onFormSuccess }) => {
  const token = localStorage.getItem("zTaskAuthToken");
  const navigate = useNavigate();
  const [masterDropdowns, setMasterDropdowns] = useState({
    customers: [],
    projects: [],
    sectors: [],
  });

  const [transactionDropdowns, setTransactionDropdowns] = useState({
    breakdownUnits: [],
    unitOptions: [],
    taxTypes: [],
  });

  const [masterFormData, setMasterFormData] = useState({
    doc_no: null,
    wo_no: null,
    wo_date: null,
    amendment_no: null,
    amendment_date: null,
    cust_id: null,
    tot_value: null,
    wo_start: null,
    wo_end: null,
    project_id: null,
    lunch: false,
    weekly_off: null,
    no_shifts: null,
    shift_hours: null,
    include_woff: false,
    kind_attn: null,
    monthly_hrs: null,
    remarks: null,
    demob_nil_after: null,
    sector: null,
    wo_open_close: "Open",
    bill_to_cust_id: null,
    project_to_id: null,
    attachment_remarks: null,
    TaxableAmt: null,
  });

  const [initialRecordFiles, setInitialRecordFiles] = useState({
    attachment_url: [],
  });
  const [masterRecordFiles, setMasterRecordFiles] = useState({
    attachment_url: [],
  });
  const [masterFormErrors, setMasterFormErrors] = useState({
    fields: [],
    error: "",
  });
  const [isLoadingMaster, setIsLoadingMaster] = useState(false);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);

  const [masterWorkOrderId, setMasterWorkOrderId] = useState(propWorkOrderId);
  const [masterWorkOrderDates, setMasterWorkOrderDates] = useState({
    wo_start: null,
    wo_end: null,
  });

  const [transactions, setTransactions] = useState([]);
  const [transactionErrors, setTransactionErrors] = useState({});

  const clearAssetAvailabilityErrors = (prev) => {
    const next = { ...prev };
    Object.keys(next).forEach((key) => {
      const errs = { ...(next[key] || {}) };
      if (
        typeof errs.asset_id === "string" &&
        errs.asset_id.includes("not available during this period")
      ) {
        delete errs.asset_id;
      }
      next[key] = errs;
    });
    return next;
  };
  const [availableAssets, setAvailableAssets] = useState({});

  const [duplicateCheckResult, setDuplicateCheckResult] = useState(null);
  const [userAcknowledgedDuplicate, setUserAcknowledgedDuplicate] = useState(false);

  const [aggregateTotals, setAggregateTotals] = useState({
    totalTaxableAmount: 0,
    totalTaxAmount: 0,
    totalAmount: 0
  });

  const showCustomAlert = (message) => {
    const alertBox = document.createElement("div");
    alertBox.className = "custom-alert";
    alertBox.innerHTML = `
      <div class="custom-alert-content">
        <p>${message}</p>
        <button onclick="this.parentNode.parentNode.remove()">OK</button>
      </div>
    `;
    document.body.appendChild(alertBox);
  };

  const checkForDuplicateWoNo = async (wo_no) => {
    if (!wo_no) return null;
    
    try {
      // FIXED: Removed ?active=1 query parameter
      const url = masterWorkOrderId
        ? `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders/check-duplicate/${encodeURIComponent(wo_no)}?id=${masterWorkOrderId}`
        : `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders/check-duplicate/${encodeURIComponent(wo_no)}`;
      
      console.log("Checking duplicate for wo_no:", wo_no);
      console.log("Request URL:", url);
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      console.log("Response status:", response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Response error:", errorText);
        throw new Error("Failed to check for duplicate work order number");
      }
      
      const data = await response.json();
      console.log("Duplicate check response:", data);
      return data;
    } catch (error) {
      console.error("Error checking for duplicate wo_no:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchAllDropdownData = async () => {
      try {
        // Load all active customers (same as Work Order List). Do not INNER JOIN
        // the Node "projects" table — new projects are created in Java "Projects"
        // and would be missed by that join.
        const customersRaw = await fetch(
          `${getNodeServiceBaseUrl()}/customer/api/customers/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const customersData = await customersRaw.json();
        const customers = mapCustomersForDropdown(customersData);
        const sectors = await getColumn("common_master", "sector");
        const breakdownUnitsRaw = await getColumn("common_master", "unit");
        const unitOptionsRaw = await getColumn("common_master", "unit");
        const taxTypesRaw = await getColumn("common_master", "tax_type");
        const asArray = (v) => (Array.isArray(v) ? v : []);

        setMasterDropdowns((prev) => ({
          ...prev,
          customers,
          sectors: asArray(sectors),
        }));
        setTransactionDropdowns({
          breakdownUnits: asArray(breakdownUnitsRaw),
          unitOptions: asArray(unitOptionsRaw),
          taxTypes: asArray(taxTypesRaw),
        });
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
        setMasterFormErrors((prevErrors) => ({
          ...prevErrors,
          error: "Failed to load form data. Please try again.",
        }));
      }
    };
    fetchAllDropdownData();
  }, [token]);

  const fetchAssetsForTransaction = useCallback(
    async (index, startDate, endDate, options = {}) => {
      const {
        excludeTranId = null,
        preserveAssetId = null,
        checkConflict = false,
      } = options;
      if (!startDate || !endDate) {
        setAvailableAssets((prev) => ({ ...prev, [index]: [] }));
        return;
      }
      try {
        const formattedStartDate = startDate;
        const formattedEndDate = endDate;
        let url = `${getAssetServiceBaseUrl()}/api/assets/for/work-order?start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
        if (excludeTranId) {
          url += `&exclude_tran_id=${excludeTranId}`;
        }

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const errorText = await response.text();
          console.error("Asset fetch failed:", errorText);
          throw new Error("Failed to fetch assets for the given date range.");
        }
        let data = await response.json();

        // Deduplicate by asset id; prefer a conflict row over an available one.
        const byId = new Map();
        for (const row of data) {
          const key = String(row.id);
          const existing = byId.get(key);
          if (!existing || row.status === "not available") {
            byId.set(key, row);
          }
        }
        data = Array.from(byId.values());

        // Keep the currently mapped asset visible even if the API omits it.
        if (
          preserveAssetId &&
          !byId.has(String(preserveAssetId))
        ) {
          try {
            const assetRes = await fetch(
              `${getAssetServiceBaseUrl()}/api/assets/${preserveAssetId}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            if (assetRes.ok) {
              const asset = await assetRes.json();
              data.push({
                id: asset.id,
                name: asset.name,
                serial_number: asset.serial_number,
                asset_code: asset.asset_code,
                registration_number: asset.registration_number,
                asset_type: asset.asset_type,
                status: "available",
                wo_id: 0,
              });
            }
          } catch (_) {
            // Non-fatal: dropdown may show placeholder but asset_id is preserved.
          }
        }

        // Other-type assets (Manpower/Diesel) are never exclusive — mark available.
        data = data.map((row) =>
          isExemptFromAssetDateConflict(row)
            ? { ...row, status: "available", wo_id: 0 }
            : row
        );

        setAvailableAssets((prev) => ({ ...prev, [index]: data }));

        if (preserveAssetId && checkConflict) {
          const assetRow = data.find(
            (row) => String(row.id) === String(preserveAssetId)
          );
          setTransactionErrors((prev) => {
            const prevErrs = { ...(prev[index] || {}) };
            if (
              assetRow &&
              !isExemptFromAssetDateConflict(assetRow) &&
              assetRow.status === "not available" &&
              String(assetRow.wo_id) !== String(masterWorkOrderId)
            ) {
              prevErrs.asset_id = `This asset is not available during this period (WO #${assetRow.wo_id}).`;
            } else if (
              typeof prevErrs.asset_id === "string" &&
              prevErrs.asset_id.includes("not available during this period")
            ) {
              delete prevErrs.asset_id;
            }
            return { ...prev, [index]: prevErrs };
          });
        }
      } catch (error) {
        console.error(`Error fetching assets for transaction ${index}:`, error);
        showCustomAlert(
          "Failed to load assets for a transaction. Please check dates and try again."
        );
        setAvailableAssets((prev) => ({ ...prev, [index]: [] }));
      }
    },
    [token, masterWorkOrderId]
  );

  useEffect(() => {
    const loadWorkOrderData = async () => {
      if (!propWorkOrderId) {
        setIsFormLoading(false);
        return;
      }
      setIsFormLoading(true);
      try {
        const masterResponse = await fetch(
          `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders/${propWorkOrderId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!masterResponse.ok) {
          throw new Error("Failed to fetch work order master data.");
        }
        const masterData = await masterResponse.json();
        let masterFiles = [];
        try {
          const masterFilesResponse = await fetch(
            `${getNodeServiceBaseUrl()}/attachment/api/attachments/${masterData.id}/work_order?active=true`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (masterFilesResponse.ok) {
            const filesPayload = await masterFilesResponse.json();
            masterFiles = Array.isArray(filesPayload)
              ? filesPayload
              : Array.isArray(filesPayload?.attachments)
                ? filesPayload.attachments
                : Array.isArray(filesPayload?.data)
                  ? filesPayload.data
                  : [];
          } else {
            console.warn(
              "Attachments fetch failed:",
              masterFilesResponse.status
            );
          }
        } catch (attachErr) {
          console.warn("Attachments fetch error:", attachErr);
          masterFiles = [];
        }

        // Format dates to input-compatible format (YYYY-MM-DD for all date fields)
        masterData.wo_date = masterData.wo_date
          ? masterData.wo_date.split("T")[0]
          : null;
        masterData.amendment_date = masterData.amendment_date
          ? masterData.amendment_date.split("T")[0]
          : null;
        masterData.wo_start = masterData.wo_start
          ? masterData.wo_start.split("T")[0]
          : null;
        masterData.wo_end = masterData.wo_end
          ? masterData.wo_end.split("T")[0]
          : null;
        // ADD THIS LINE - Keep add_date as is (it's already in ISO format for display)
        // No need to format it here, we'll format it in the display using formatDateTimeToDDMMYYYY

        setInitialRecordFiles({
          attachment_url: masterFiles,
        });
        setMasterRecordFiles({
          attachment_url: masterFiles,
        });
        setMasterFormData(masterData);
        setMasterWorkOrderId(propWorkOrderId);
        setMasterWorkOrderDates({
          wo_start: masterData.wo_start,
          wo_end: masterData.wo_end,
        });

        if (masterData.cust_id) {
          const projects = await fetchProjectsForCustomer(
            masterData.cust_id,
            token
          );
          setMasterDropdowns((prev) => ({ ...prev, projects }));
        }

        const tranResponse = await fetch(
          `${getNodeServiceBaseUrl()}/work-order/api/work-order-tran/${propWorkOrderId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (!tranResponse.ok) {
          throw new Error("Failed to fetch work order transactions.");
        }
        const tranPayload = await tranResponse.json();
        const tranData = Array.isArray(tranPayload)
          ? tranPayload
          : Array.isArray(tranPayload?.data)
            ? tranPayload.data
            : [];
        const formattedTransactions = tranData.map((tran) => {
  // ADD THIS: Calculate tax_amount if not present
  const taxable_amt = parseFloat(tran.taxable_amt || 0);
  const tax_percentage = parseFloat(tran.tax_percentage || 0);
  const tax_amount = (taxable_amt * tax_percentage) / 100;
  
  return {
    ...tran,
    id: tran.id,
    dbId: tran.id,
    start_date: tran.start_date
      ? tran.start_date.split("T")[0]  // Changed: removed toISTInputValue and slice(0, 16)
      : null,
    end_date: tran.end_date
      ? tran.end_date.split("T")[0]    // Changed: removed toISTInputValue and slice(0, 16)
      : null,
    original_start_date: tran.start_date
      ? tran.start_date.split("T")[0]
      : null,
    original_end_date: tran.end_date
      ? tran.end_date.split("T")[0]
      : null,
    rate_per_hr: parseFloat(tran.rate_per_hr || 0),
    daily_rate: parseFloat(tran.daily_rate || 0),
    monthly_rate: parseFloat(tran.monthly_rate || 0),
    monthly_working_days: parseInt(tran.monthly_working_days || 30),
    quantity: parseInt(tran.quantity || 1),
    unit: tran.unit || null,
    rate: parseFloat(tran.rate || 0),
    mob: parseFloat(tran.mob || 0),
    demob: parseFloat(tran.demob || 0),
    amount: parseFloat(tran.amount || 0),
    working_hrs: parseInt(tran.working_hrs || 0),
    breakdown_penalty: parseFloat(tran.breakdown_penalty || 0),
    tax_type: tran.tax_type || null,
    tax_percentage: parseFloat(tran.tax_percentage || 0),
    taxable_amt: parseFloat(tran.taxable_amt || 0),
    tax_amount: parseFloat(tax_amount.toFixed(2)), // CALCULATED TAX AMOUNT
    total_amount: parseFloat(tran.total_amount || 0),
  };
});

        setTransactions(formattedTransactions);

        // ADD THIS: Calculate aggregate totals after setting transactions
        const aggregates = calculateAggregateTotals(formattedTransactions);
        setAggregateTotals(aggregates);

        formattedTransactions.forEach(async (tran, index) => {
          if (tran.start_date && tran.end_date) {
            await fetchAssetsForTransaction(
              index,
              tran.start_date,
              tran.end_date,
              {
                excludeTranId: tran.dbId,
                preserveAssetId: tran.asset_id,
                checkConflict: false,
              }
            );
          }
        });
      } catch (error) {
        console.error("Error loading work order data:", error);
        showCustomAlert("Failed to load work order details. Please try again.");
      } finally {
        setIsFormLoading(false);
      }
    };

    loadWorkOrderData();
  }, [propWorkOrderId, fetchAssetsForTransaction, token]);

  const getInvalidFields = (updatedData) => {
    let newErrorsFields = [];
    let newGeneralErrorMessage = "";

    const noShiftsForCalc = parseFloat(updatedData.no_shifts) || 0;
    const shiftHoursForCalc = parseFloat(updatedData.shift_hours) || 0;
    const includeWoff = updatedData.include_woff;
    const weeklyOffSelected = updatedData.weekly_off;

    let monthlyHours = 0;
    if (noShiftsForCalc > 0 && shiftHoursForCalc > 0) {
      const dailyHours = noShiftsForCalc * shiftHoursForCalc;
      const workingDaysInMonth = includeWoff && weeklyOffSelected ? 26 : 30;
      monthlyHours = dailyHours * workingDaysInMonth;
    }
    updatedData.monthly_hrs = monthlyHours > 0 ? monthlyHours.toFixed(2) : null;

    const woStartObj = updatedData.wo_start
      ? new Date(updatedData.wo_start + 'T00:00:00')
      : null;
    const woEndObj = updatedData.wo_end 
      ? new Date(updatedData.wo_end + 'T23:59:59') 
      : null;

    if (woStartObj && woEndObj && woEndObj.getTime() < woStartObj.getTime()) {
      if (!newErrorsFields.includes("wo_end")) newErrorsFields.push("wo_end");
      newGeneralErrorMessage =
        "Work Order End Date cannot be before Work Order Start Date.";
    }
    return {
      newErrorsFields,
      newGeneralErrorMessage,
      updatedData,
    };
  };

  const handleMasterChange = async (e) => {
    const { name, value, type, checked, files } = e.target;

    let updatedData = {
      ...masterFormData,
      [name]: type === "checkbox" ? checked : value === "" ? null : value,
    };

    if (type === "file") {
      console.log("File input detected. Files:", files);
      if (files && files.length > 0) {
        console.log("Number of files selected:", files.length);
        console.log("First file name:", files[0].name);
      } else {
        console.log("No files selected or files object is empty.");
      }
      setMasterRecordFiles((prev) => {
        const newState = {
          ...prev,
          [name]: Array.from(files),
        };
        console.log("masterRecordFiles updated to:", newState);
        return newState;
      });
      return;
    }

    // REMOVED: Real-time duplicate checking - will only check on submit
    // Just clear any previous duplicate warnings when wo_no changes
    if (name === "wo_no") {
      setDuplicateCheckResult(null);
      setUserAcknowledgedDuplicate(false);
    }

    if (e.target.type === "number") {
      updatedData[name] = value === "" ? null : parseFloat(value);
    }
    if (e.target.type === "date") {
      updatedData[name] = value === "" ? null : value;
    }

    let updatedFormErrorsFields = masterFormErrors.fields.filter(
      (field) =>
        ![
          "no_shifts",
          "shift_hours",
          "wo_date",
          "wo_start",
          "wo_end",
          "cust_id",
          "project_id",
          "sector",
        ].includes(field)
    );

    const currentNoShifts = parseFloat(updatedData.no_shifts);
    if (name === "no_shifts") {
      if (!value || isNaN(currentNoShifts)) {
        updatedData.shift_hours = null;
      } else {
        if (currentNoShifts === 1) {
          updatedData.shift_hours = 24;
        } else if (currentNoShifts === 2) {
          updatedData.shift_hours = 12;
        } else if (currentNoShifts === 3) {
          updatedData.shift_hours = 8;
        }
      }
    }

    const validations = getInvalidFields(updatedData);
    updatedData = validations.updatedData;
    let newErrorsFields = validations.newErrorsFields;
    let newGeneralErrorMessage = validations.newGeneralErrorMessage;

    if (name === "cust_id") {
      updatedData.project_id = null;
      try {
        const projects = value
          ? await fetchProjectsForCustomer(value, token)
          : [];
        setMasterDropdowns((prevDropdowns) => ({ ...prevDropdowns, projects }));
      } catch (error) {
        console.error("Error fetching projects:", error);
        setMasterFormErrors((prevErrors) => ({
          ...prevErrors,
          error: "Failed to load projects for selected customer.",
        }));
        setMasterDropdowns((prevDropdowns) => ({
          ...prevDropdowns,
          projects: [],
        }));
      }
    }

    const requiredFields = [
      "wo_no",
      "wo_date",
      "wo_start",
      "wo_end",
      "cust_id",
      "project_id",
      "no_shifts",
      "sector",
    ];
    requiredFields.forEach((field) => {
      if (!updatedData[field] && updatedData[field] !== false) {
        if (!newErrorsFields.includes(field)) {
          newErrorsFields.push(field);
        }
        if (!newGeneralErrorMessage) {
          newGeneralErrorMessage = "Please fill in all required fields.";
        }
      } else {
        updatedFormErrorsFields = updatedFormErrorsFields.filter(
          (f) => f !== field
        );
      }
    });

    setMasterFormData(updatedData);

    if (name === "wo_start" || name === "wo_end") {
      setMasterWorkOrderDates({
        wo_start: updatedData.wo_start,
        wo_end: updatedData.wo_end,
      });
      // Clear stale master-boundary and asset availability errors after period change.
      setTransactionErrors((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          const errs = { ...(next[key] || {}) };
          if (
            typeof errs.start_date === "string" &&
            errs.start_date.includes("Work Order Master")
          ) {
            delete errs.start_date;
          }
          if (
            typeof errs.end_date === "string" &&
            errs.end_date.includes("Work Order Master")
          ) {
            delete errs.end_date;
          }
          if (
            typeof errs.asset_id === "string" &&
            errs.asset_id.includes("not available during this period")
          ) {
            delete errs.asset_id;
          }
          next[key] = errs;
        });
        return next;
      });
    }

    if (newErrorsFields.length == 0) {
      setMasterFormErrors({
        fields: newErrorsFields,
        error: newGeneralErrorMessage,
      });
    }
  };

  const handleMasterSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingMaster(true);

    let updatedData = masterFormData;

    // Perform basic validation first
    const validations = getInvalidFields(updatedData);
    updatedData = validations.updatedData;
    let newErrorsFields = validations.newErrorsFields;
    let newGeneralErrorMessage = validations.newGeneralErrorMessage;

    if (newErrorsFields.length > 0 || newGeneralErrorMessage) {
      setMasterFormErrors((prev) => ({
        fields: [...prev.fields, ...newErrorsFields],
        error: newGeneralErrorMessage,
      }));
      setIsLoadingMaster(false);
      return;
    }

    // IMPROVED: Check for duplicate wo_no before submission (only once)
    if (updatedData.wo_no && !userAcknowledgedDuplicate) {
      console.log("Checking for duplicate work order number:", updatedData.wo_no);
      const duplicateResult = await checkForDuplicateWoNo(updatedData.wo_no);
      
      if (duplicateResult && duplicateResult.isDuplicate) {
        setDuplicateCheckResult(duplicateResult);
        setIsLoadingMaster(false);
        
        const existingRecords = duplicateResult.existingRecords
          .map(record => `\n  • Doc No: ${record.doc_no} (ID: ${record.id})`)
          .join("");
        
        const userConfirmed = window.confirm(
          `⚠️ DUPLICATE WORK ORDER NUMBER DETECTED!\n\n` +
          `Work Order Number "${updatedData.wo_no}" already exists in:${existingRecords}\n\n` +
          `⚠️ Creating a duplicate work order number may cause confusion and tracking issues.\n\n` +
          `Do you still want to proceed with creating this work order?`
        );
        
        if (!userConfirmed) {
          showCustomAlert("Work order creation cancelled due to duplicate work order number.");
          setDuplicateCheckResult(null);
          setUserAcknowledgedDuplicate(false);
          return;
        }
        
        // User confirmed - set flag and continue with submission
        setUserAcknowledgedDuplicate(true);
      }
    }

    // Proceed with work order creation
    try {
      // Convert date fields to datetime for backend
      const payloadData = {
  ...masterFormData,
  wo_start: masterFormData.wo_start ? `${masterFormData.wo_start}T00:00:00.000Z` : null,
  wo_end: masterFormData.wo_end ? `${masterFormData.wo_end}T23:59:59.999Z` : null,
  wo_date: masterFormData.wo_date ? `${masterFormData.wo_date}T00:00:00.000Z` : null,
  amendment_date: masterFormData.amendment_date ? `${masterFormData.amendment_date}T00:00:00.000Z` : null,
};

      const response = await fetch(
        `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payloadData),
        }
      );

      if (response.ok) {
        console.log("Work Order created successfully:", masterFormData);
        showCustomAlert("Work Order created successfully!");

        const result = await response.json();
        const woId = result.id;
        const docNo = result.doc_no || masterFormData.doc_no;
        
        // Reset duplicate check state after successful submission
        setDuplicateCheckResult(null);
        setUserAcknowledgedDuplicate(false);
        
        // FIXED: Only upload files if there are actual new files
        if (hasNewFilesToUpload(masterRecordFiles)) {
          const formData = new FormData();
          
          for (const fileField in masterRecordFiles) {
            const files = masterRecordFiles[fileField];
            
            if (files instanceof FileList) {
              for (let i = 0; i < files.length; i++) {
                formData.append("files", files[i]);
              }
            } else if (Array.isArray(files)) {
              files.forEach(file => {
                if (file instanceof File && !file.file_url) {
                  formData.append("files", file);
                }
              });
            } else if (files instanceof File) {
              formData.append("files", files);
            }
          }

          if (formData.has("files")) {
            const res = await fetch(
              `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/attachment/api/attachments/${woId}/work_order`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${token}`,
                },
                body: formData,
              }
            );
            
            if (res.ok) {
              showCustomAlert("Files uploaded successfully.");
              setMasterRecordFiles({ attachment_url: [] });
              setInitialRecordFiles({ attachment_url: [] });
            } else {
              const error = await res.json();
              console.log(error);
              showCustomAlert(
                `Failed to upload files. Error: ${error.error}.`
              );
            }
          }
        }
        
        setMasterWorkOrderId(woId);
        setMasterFormData((prevData) => ({
          ...prevData,
          doc_no: docNo,
        }));
        setMasterWorkOrderDates({
          wo_start: masterFormData.wo_start,
          wo_end: masterFormData.wo_end,
        });
        setMasterFormErrors({ fields: [], error: "" });

        if (onFormSuccess) onFormSuccess(woId);
        navigate("/work-orders");
      } else {
        const errorData = await response.json();
        console.error("API Error:", errorData);

        if (errorData.fields && Array.isArray(errorData.fields)) {
          setMasterFormErrors((prevErrors) => ({
            fields: [...new Set([...prevErrors.fields, ...errorData.fields])],
            error: errorData.error || "Please correct the highlighted fields.",
          }));
        } else {
          setMasterFormErrors((prevErrors) => ({
            ...prevErrors,
            error:
              errorData.error ||
              "An unexpected error occurred during submission.",
          }));
        }
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
      setMasterFormErrors((prevErrors) => ({
        ...prevErrors,
        error:
          "Failed to connect to the server. Please check your internet connection.",
      }));
    } finally {
      setIsLoadingMaster(false);
    }
  };

  const handleMasterUpdate = async (e, updatedValue = {}) => {
    if (e) e.preventDefault();
    setIsLoadingMaster(true);

    // Perform basic validation first
    const validations = getInvalidFields(updatedValue);
    updatedValue = validations.updatedData;
    let newErrorsFields = validations.newErrorsFields;
    let newGeneralErrorMessage = validations.newGeneralErrorMessage;

    if (newErrorsFields.length > 0 || newGeneralErrorMessage) {
      setMasterFormErrors((prev) => ({
        fields: [...prev.fields, ...newErrorsFields],
        error: newGeneralErrorMessage,
      }));
      setIsLoadingMaster(false);
      return;
    }

    // IMPROVED: Check for duplicate wo_no before update (only if wo_no is being changed and user initiated the action)
    if (e && (updatedValue.wo_no || masterFormData.wo_no) && !userAcknowledgedDuplicate) {
      const woNoToCheck = updatedValue.wo_no || masterFormData.wo_no;
      console.log("Checking for duplicate work order number:", woNoToCheck);
      const duplicateResult = await checkForDuplicateWoNo(woNoToCheck);
      
      if (duplicateResult && duplicateResult.isDuplicate) {
        setDuplicateCheckResult(duplicateResult);
        setIsLoadingMaster(false);
        
        const existingRecords = duplicateResult.existingRecords
          .map(record => `\n  • Doc No: ${record.doc_no} (ID: ${record.id})`)
          .join("");
        
        const userConfirmed = window.confirm(
          `⚠️ DUPLICATE WORK ORDER NUMBER DETECTED!\n\n` +
          `Work Order Number "${woNoToCheck}" already exists in:${existingRecords}\n\n` +
          `⚠️ Using a duplicate work order number may cause confusion and tracking issues.\n\n` +
          `Do you still want to proceed with updating to this work order number?`
        );
        
        if (!userConfirmed) {
          showCustomAlert("Work order update cancelled due to duplicate work order number.");
          setDuplicateCheckResult(null);
          setUserAcknowledgedDuplicate(false);
          return;
        }
        
        // User confirmed - set flag and continue
        setUserAcknowledgedDuplicate(true);
      }
    }

    // Proceed with update
    try {
      // Convert date fields to datetime for backend
      const payload = {
  ...masterFormData,
  ...updatedValue,
  wo_start: (updatedValue.wo_start || masterFormData.wo_start) 
    ? `${updatedValue.wo_start || masterFormData.wo_start}T00:00:00.000Z` 
    : null,
  wo_end: (updatedValue.wo_end || masterFormData.wo_end)
    ? `${updatedValue.wo_end || masterFormData.wo_end}T23:59:59.999Z`
    : null,
  wo_date: (updatedValue.wo_date || masterFormData.wo_date)
    ? `${updatedValue.wo_date || masterFormData.wo_date}T00:00:00.000Z`
    : null,
  amendment_date: (updatedValue.amendment_date || masterFormData.amendment_date)
    ? `${updatedValue.amendment_date || masterFormData.amendment_date}T00:00:00.000Z`
    : null,
};

      const response = await fetch(
        `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-orders/${masterWorkOrderId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        // Reset duplicate check state after successful update
        setDuplicateCheckResult(null);
        setUserAcknowledgedDuplicate(false);
        
        if (
          e == null ||
          (Object.keys(updatedValue).length == 2 &&
            Object.hasOwn(updatedValue, "tot_value") &&
            Object.hasOwn(updatedValue, "TaxableAmt"))
        ) {
          setMasterFormData(payload);
        } else {
          console.log("Work Order updated successfully:", payload);
          showCustomAlert("Work Order updated successfully!");
          const result = await response.json();
          const woId = result.id;
          
          // FIXED: Only upload files if there are actual new files
          if (hasNewFilesToUpload(masterRecordFiles)) {
            const formData = new FormData();
            
            for (const fileField in masterRecordFiles) {
              const files = masterRecordFiles[fileField];
              
              if (files instanceof FileList) {
                for (let i = 0; i < files.length; i++) {
                  formData.append("files", files[i]);
                }
              } else if (Array.isArray(files)) {
                files.forEach(file => {
                  if (file instanceof File && !file.file_url) {
                    formData.append("files", file);
                  }
                });
              } else if (files instanceof File) {
                formData.append("files", files);
              }
            }

            if (formData.has("files")) {
              const res = await fetch(
                `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/attachment/api/attachments/${woId}/work_order`,
                {
                  method: "POST",
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                  body: formData,
                }
              );
              
              if (res.ok) {
                showCustomAlert("Files uploaded successfully.");
                const updatedFilesResponse = await fetch(
  `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/attachment/api/attachments/${woId}/work_order?active=true`,
  {
    headers: { Authorization: `Bearer ${token}` },
  }
);
                const updatedFiles = await updatedFilesResponse.json();
                setMasterRecordFiles({ attachment_url: updatedFiles });
                setInitialRecordFiles({ attachment_url: updatedFiles });
              } else {
                const error = await res.json();
                showCustomAlert(
                  `Failed to upload files. Error: ${error.error}.`
                );
              }
            }
          }
          
          const normalizedPayload = {
            ...payload,
            wo_start: payload.wo_start ? String(payload.wo_start).split("T")[0] : null,
            wo_end: payload.wo_end ? String(payload.wo_end).split("T")[0] : null,
            wo_date: payload.wo_date ? String(payload.wo_date).split("T")[0] : null,
            amendment_date: payload.amendment_date
              ? String(payload.amendment_date).split("T")[0]
              : null,
          };

          setMasterWorkOrderDates({
            wo_start: normalizedPayload.wo_start,
            wo_end: normalizedPayload.wo_end,
          });
          setMasterFormData(normalizedPayload);
          setMasterFormErrors({ fields: [], error: "" });
          setTransactionErrors(clearAssetAvailabilityErrors);
          if (onFormSuccess) onFormSuccess(masterWorkOrderId);
        }
      } else {
        const errorData = await response.json();
        console.error("API Error during update:", errorData);

        if (errorData.fields && Array.isArray(errorData.fields)) {
          setMasterFormErrors((prevErrors) => ({
            fields: [...new Set([...prevErrors.fields, ...errorData.fields])],
            error: errorData.error || "Please correct the highlighted fields.",
          }));
        } else {
          setMasterFormErrors((prevErrors) => ({
            ...prevErrors,
            error:
              errorData.error || "An unexpected error occurred during update.",
          }));
        }
      }
    } catch (error) {
      console.error("Network or unexpected error during update:", error);
      setMasterFormErrors((prevErrors) => ({
        ...prevErrors,
        error:
          "Failed to connect to the server. Please check your internet connection.",
      }));
    } finally {
      setIsLoadingMaster(false);
    }
  };

 const calculateTransactionAmounts = (transaction) => {
  const quantity = parseInt(transaction.quantity) || 0;
  const rate = parseFloat(transaction.rate) || 0;
  const mob = parseFloat(transaction.mob) || 0;
  const demob = parseFloat(transaction.demob) || 0;
  const taxPercentage = parseFloat(transaction.tax_percentage) || 0;
  const workingHrs = parseFloat(transaction.working_hrs) || 8;
  const monthlyWorkingDays = parseInt(transaction.monthly_working_days) || 30;
  const unit = transaction.unit;

  let rate_per_hr = 0;
  let daily_rate = 0;
  let monthly_rate = 0;

  // Calculate rates based on the unit type
  if (unit && rate > 0) {
    // Get the unit value from dropdown to determine calculation
    // IDs from API are often strings; transaction.unit may be number — use loose match
    const unitValue =
      transactionDropdowns.unitOptions?.find(
        (u) => String(u.id) === String(unit)
      )?.value || "";
    const unitLower = String(unitValue).toLowerCase();

    if (unitLower.includes("month")) {
      // User entered monthly rate
      monthly_rate = rate;
      daily_rate = monthly_rate / monthlyWorkingDays;
      rate_per_hr = daily_rate / workingHrs;
    } else if (unitLower.includes("day")) {
      // User entered daily rate
      daily_rate = rate;
      rate_per_hr = daily_rate / workingHrs;
      monthly_rate = daily_rate * monthlyWorkingDays;
    } else if (unitLower.includes("hour") || unitLower.includes("hr")) {
      // User entered hourly rate
      rate_per_hr = rate;
      daily_rate = rate_per_hr * workingHrs;
      monthly_rate = daily_rate * monthlyWorkingDays;
    } else if (unitLower.includes("lumpsum") || unitLower.includes("lump")) {
      // Lumpsum: All rates are the same as entered rate
      rate_per_hr = rate;
      daily_rate = rate;
      monthly_rate = rate;
    } else {
      // Default: treat as monthly rate
      monthly_rate = rate;
      daily_rate = monthly_rate / monthlyWorkingDays;
      rate_per_hr = daily_rate / workingHrs;
    }
  }

  // Calculate base amount: quantity × rate
  const base_amount = quantity * rate;
  
  // Calculate taxable amount (add MOB and DEMOB)
  const taxable_amt = base_amount + mob + demob;
  
  // Calculate tax amount
  const tax_amount = (taxable_amt * taxPercentage) / 100;
  
  // Calculate total amount
  const total_amount = taxable_amt + tax_amount;
  
  return {
    rate_per_hr: parseFloat(rate_per_hr.toFixed(2)),
    daily_rate: parseFloat(daily_rate.toFixed(2)),
    monthly_rate: parseFloat(monthly_rate.toFixed(2)),
    taxable_amt: parseFloat(taxable_amt.toFixed(2)),
    tax_amount: parseFloat(tax_amount.toFixed(2)),
    total_amount: parseFloat(total_amount.toFixed(2)),
  };
};

// ADD THIS HELPER FUNCTION after calculateTransactionAmounts function
const calculateAggregateTotals = (transactions) => {
  const totals = transactions.reduce((acc, tran) => {
    return {
      totalTaxableAmount: acc.totalTaxableAmount + parseFloat(tran.taxable_amt || 0),
      totalTaxAmount: acc.totalTaxAmount + parseFloat(tran.tax_amount || 0),
      totalAmount: acc.totalAmount + parseFloat(tran.total_amount || 0)
    };
  }, {
    totalTaxableAmount: 0,
    totalTaxAmount: 0,
    totalAmount: 0
  });
  
  return {
    totalTaxableAmount: totals.totalTaxableAmount.toFixed(2),
    totalTaxAmount: totals.totalTaxAmount.toFixed(2),
    totalAmount: totals.totalAmount.toFixed(2)
  };
};

  const saveSingleTransaction = async (tranToSave, index) => {
    let isValid = true;
    const errors = {};

    if (tranToSave.asset_id === null || tranToSave.asset_id === "") {
      errors.asset_id = "Required.";
      isValid = false;
    }
    if (tranToSave.start_date === null || tranToSave.start_date === "") {
      errors.start_date = "Required.";
      isValid = false;
    }
    if (tranToSave.end_date === null || tranToSave.end_date === "") {
      errors.end_date = "Required.";
      isValid = false;
    }
    if (
      tranToSave.working_hrs === null ||
      isNaN(tranToSave.working_hrs) ||
      tranToSave.working_hrs <= 0 ||
      tranToSave.working_hrs > 24
    ) {
      errors.working_hrs = "Must be between 1 and 24 hours.";
      isValid = false;
    }
    if (tranToSave.hsd_scope === null || tranToSave.hsd_scope === "") {
      errors.hsd_scope = "Required.";
      isValid = false;
    }
    if (
      tranToSave.quantity === null ||
      tranToSave.quantity === "" ||
      isNaN(parseFloat(tranToSave.quantity)) ||
      tranToSave.quantity <= 0
    ) {
      errors.quantity = "Must be a positive number.";
      isValid = false;
    }
    // Unit validation
    if (!tranToSave.unit || tranToSave.unit === "") {
      errors.unit = "Unit is required.";
      isValid = false;
    }

    // Rate validation
if (tranToSave.rate === null || tranToSave.rate === "" || 
    isNaN(parseFloat(tranToSave.rate)) || parseFloat(tranToSave.rate) < 0) {
  errors.rate = "Rate is required and must be non-negative.";
  isValid = false;
} else {
  // Recalculate rates before validation to ensure they're populated
  const calculatedAmounts = calculateTransactionAmounts(tranToSave);
  tranToSave.rate_per_hr = calculatedAmounts.rate_per_hr;
  tranToSave.daily_rate = calculatedAmounts.daily_rate;
  tranToSave.monthly_rate = calculatedAmounts.monthly_rate;
}

    // Tax validation (optional fields)
    if (tranToSave.tax_type && !['CGST-SGST', 'IGST'].includes(tranToSave.tax_type)) {
      errors.tax_type = "Invalid tax type.";
      isValid = false;
    }

    if (tranToSave.tax_percentage !== null && 
        tranToSave.tax_percentage !== "" && 
        (isNaN(parseFloat(tranToSave.tax_percentage)) || 
         tranToSave.tax_percentage < 0 || 
         tranToSave.tax_percentage > 100)) {
      errors.tax_percentage = "Tax percentage must be between 0 and 100.";
      isValid = false;
    }

    // Date validation — use current master form dates so extending WO end date
    // in the header applies to transaction validation before master is saved.
    const masterStartDateOnly =
      masterFormData.wo_start ?? masterWorkOrderDates.wo_start ?? null;
    const masterEndDateOnly =
      masterFormData.wo_end ?? masterWorkOrderDates.wo_end ?? null;

    const tranStartDate = tranToSave.start_date ? new Date(tranToSave.start_date + 'T00:00:00') : null;
    const tranEndDate = tranToSave.end_date ? new Date(tranToSave.end_date + 'T23:59:59') : null;

    const masterStart = masterStartDateOnly ? new Date(masterStartDateOnly + 'T00:00:00') : null;
    const masterEnd = masterEndDateOnly ? new Date(masterEndDateOnly + 'T23:59:59') : null;

    if (tranStartDate && masterStart && tranStartDate < masterStart) {
      errors.start_date = "Must be after Work Order Master Start Date.";
      isValid = false;
    } else if (tranStartDate && masterEnd && tranStartDate > masterEnd) {
      errors.start_date = "Must be before Work Order Master End Date.";
      isValid = false;
    }

    if (tranEndDate && masterStart && tranEndDate < masterStart) {
      errors.end_date = "Must be after Work Order Master Start Date.";
      isValid = false;
    } else if (tranEndDate && masterEnd && tranEndDate > masterEnd) {
      errors.end_date = "Must be before Work Order Master End Date.";
      isValid = false;
    } else if (tranStartDate && tranEndDate && tranEndDate < tranStartDate) {
      errors.end_date = "Must be after Transaction Start Date.";
      isValid = false;
    }

    const isDuplicateAsset = transactions.some(
      (t, i) =>
        i !== index &&
        t.asset_id !== null &&
        t.asset_id === tranToSave.asset_id
    );
    if (tranToSave.asset_id !== null && isDuplicateAsset) {
      errors.asset_id = "This asset is already used in another transaction.";
      isValid = false;
    }

    setTransactionErrors((prev) => ({ ...prev, [index]: errors }));

    if (!isValid) {
      return {
        success: false,
        message: "Validation failed for transaction.",
        errors: errors,
      };
    }

    try {
      const payload = {
  ...tranToSave,
  wo_id: masterWorkOrderId,
  original_start_date: tranToSave.original_start_date || tranToSave.start_date,
  original_end_date: tranToSave.original_end_date || tranToSave.end_date,
  // Ensure all rate fields are included
  rate_per_hr: tranToSave.rate_per_hr || 0,
  daily_rate: tranToSave.daily_rate || 0,
  monthly_rate: tranToSave.monthly_rate || 0,
  tax_type: tranToSave.tax_type || null,
  tax_percentage: tranToSave.tax_percentage || 0,
  taxable_amt: tranToSave.taxable_amt || 0,
  tax_amount: tranToSave.tax_amount || 0,
  total_amount: tranToSave.total_amount || 0,
};

      Object.keys(payload).forEach((key) => {
        if (typeof payload[key] === "string" && payload[key].trim() === "") {
          payload[key] = null;
        } else if (typeof payload[key] === "number" && isNaN(payload[key])) {
          payload[key] = null;
        }
      });

      const method = tranToSave.dbId ? "PUT" : "POST";
      const url = tranToSave.dbId
        ? `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-order-tran/${tranToSave.dbId}`
        : `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-order-tran/`;

      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const result = await response.json();
        setTransactions((prev) =>
          prev.map((t, i) =>
            i === index
              ? {
                  ...t,
                  dbId: result.id || t.dbId,
                  // Lock current dates as the last successfully submitted period.
                  original_start_date: t.start_date,
                  original_end_date: t.end_date,
                }
              : t
          )
        );
        setTransactionErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[index];
          return newErrors;
        });
        return { success: true, message: "Transaction saved successfully." };
      } else {
        const errorData = await response.json();
        console.error(
          `Error saving transaction (index ${index}) for asset ${tranToSave.asset_id}:`,
          errorData
        );
        showCustomAlert(
          `Failed to save transaction. Error: ${
            errorData.error || "Unknown error"
          }`
        );
        if (errorData.fields) {
          setTransactionErrors((prev) => ({
            ...prev,
            [index]: { ...errors, ...errorData.fields },
          }));
        }
        return {
          success: false,
          message: errorData.error || "API error during save.",
        };
      }
    } catch (error) {
      console.error(
        `Network or unexpected error saving transaction (index ${index}):`,
        error
      );
      showCustomAlert(
        "Failed to connect to the server while saving transaction."
      );
      return { success: false, message: "Network error." };
    }
  };

  const resetTransactionDates = (index) => {
    const tran = transactions[index];
    if (!tran?.original_start_date || !tran?.original_end_date) {
      showCustomAlert(
        "No previously saved dates are available to restore for this transaction."
      );
      return;
    }

    const restoredStart = tran.original_start_date;
    const restoredEnd = tran.original_end_date;

    setTransactions((prev) =>
      prev.map((t, i) =>
        i === index
          ? { ...t, start_date: restoredStart, end_date: restoredEnd }
          : t
      )
    );

    setTransactionErrors((prev) => {
      const next = { ...(prev[index] || {}) };
      delete next.start_date;
      delete next.end_date;
      if (
        typeof next.asset_id === "string" &&
        next.asset_id.includes("not available during this period")
      ) {
        delete next.asset_id;
      }
      return { ...prev, [index]: next };
    });

    fetchAssetsForTransaction(index, restoredStart, restoredEnd, {
      excludeTranId: tran.dbId,
      preserveAssetId: tran.asset_id,
      checkConflict: false,
    });
  };

  const addTransaction = async () => {
    if (transactions.length > 0) {
      const lastTransactionIndex = transactions.length - 1;
      const lastTransaction = transactions[lastTransactionIndex];

      if (!lastTransaction.dbId) {
        setIsLoadingTransactions(true);
        const saveResult = await saveSingleTransaction(
          lastTransaction,
          lastTransactionIndex
        );
        setIsLoadingTransactions(false);

        if (!saveResult.success) {
          showCustomAlert(
            "Please correct the last transaction before adding a new one."
          );
          return;
        } else {
          if (!saveResult.message.includes("successfully")) {
            showCustomAlert("Previous transaction saved. Adding new row.");
          }
        }
      }
    }
    await fetchAssetsForTransaction(
      transactions.length,
      masterFormData.wo_start,
      masterFormData.wo_end
    );

    setTransactions((prevTransactions) => {
      const newTran = {
  id: Date.now(),
  dbId: null,
  wo_id: masterWorkOrderId,
  wo_sr_no: prevTransactions.length + 1,
  asset_id: null,
  start_date: masterFormData.wo_start || null,
  end_date: masterFormData.wo_end || null,
  rate_per_hr: 0,
  daily_rate: 0,
  monthly_rate: 0,
  monthly_working_days: 30,
  quantity: 1,
  unit: null,
  rate: null,
  mob: null,
  demob: null,
  amount: 0,
  working_hrs: masterFormData.shift_hours,
  breakdown_penalty: null,
  breakdown_penalty_unit: null,
  original_start_date: null,
  original_end_date: null,
  main_equipment: false,
  hsd_scope: null,
  tax_type: null,
  tax_percentage: 0,
  taxable_amt: 0,
  tax_amount: 0,
  total_amount: 0,
};
      setTransactionErrors((prev) => ({
        ...prev,
        [prevTransactions.length]: {},
      }));
      return [...prevTransactions, newTran];
    });
  };

  const removeTransaction = async (indexToRemove, dbId) => {
  if (dbId) {
    if (
      window.confirm(
        "Are you sure you want to delete this transaction from the database?"
      )
    ) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/work-order/api/work-order-tran/${dbId}`,
          {
            method: "DELETE",
            headers: { 
              "Authorization": `Bearer ${token}`,
              "Content-Type": "application/json"  // Add this
            },
          }
        );
        
        // Handle non-JSON responses (HTML error pages)
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          console.error("Server returned non-JSON response:", await response.text());
          showCustomAlert(
            "Server error occurred. Please check the console for details."
          );
          return;
        }
        
        const errorData = await response.json();
        
        if (!response.ok) {
          showCustomAlert(
            `Failed to delete transaction: ${errorData.error || "Unknown error"}`
          );
          return;
        }
        
        showCustomAlert("Transaction deleted from database successfully.");
      } catch (error) {
        console.error("Error deleting transaction:", error);
        showCustomAlert(
          "Network error while deleting transaction. Please try again."
        );
        return;
      }
    } else {
      return;
    }
  }

  // Rest of your code for removing from UI...
  const updatedTransactions = transactions.filter(
    (_, i) => i !== indexToRemove
  );
  setTransactions(updatedTransactions);

  // ADD THIS: Recalculate aggregates
  const aggregates = calculateAggregateTotals(updatedTransactions);
  setAggregateTotals(aggregates);

  const newTransactionErrors = { ...transactionErrors };
  delete newTransactionErrors[indexToRemove];
  setTransactionErrors(newTransactionErrors);

  const newAvailableAssets = { ...availableAssets };
  delete newAvailableAssets[indexToRemove];
  setAvailableAssets(newAvailableAssets);

  const totalTaxableAmount = updatedTransactions.reduce(
    (sum, tran) => sum + parseFloat(tran.taxable_amt || 0),
    0
  );
  const totalAmount = updatedTransactions.reduce(
    (sum, tran) => sum + parseFloat(tran.total_amount || 0),
    0
  );
  handleMasterUpdate(null, { TaxableAmt: aggregates.totalTaxableAmount, tot_value: aggregates.totalAmount });
};

  const handleChangeTransaction = async (index, e) => {
  const { name, value, type, checked } = e.target;
  const updatedTransactions = [...transactions];
  const currentTransaction = { ...updatedTransactions[index] };

  let newErrors = { ...(transactionErrors[index] || {}) };

  // Handle different field types
  if (type === "checkbox") {
    currentTransaction[name] = checked;
  } else if (type === "number") {
    const parsedValue = value === "" ? null : parseFloat(value);
    currentTransaction[name] = parsedValue;
  } else {
    currentTransaction[name] = value === "" ? null : value;
  }

  // ✅ SPECIAL HANDLING: Convert quantity to integer
  if (name === "quantity") {
    const parsedValue = value === "" ? null : parseInt(value);
    if (parsedValue !== null && (parsedValue <= 0 || isNaN(parsedValue))) {
      newErrors.quantity = "Must be a positive whole number.";
    } else {
      delete newErrors.quantity;
    }
    currentTransaction.quantity = parsedValue;
  }

  // Validation for working_hrs
  if (name === "working_hrs") {
    const parsedValue = parseFloat(value);
    if (value === "" || isNaN(parsedValue) || parsedValue <= 0 || parsedValue > 24) {
      newErrors.working_hrs = "Must be between 1 and 24 hours.";
    } else {
      delete newErrors.working_hrs;
    }
  }

  // Handle asset_id_combobox
  if (name === "asset_id_combobox") {
    currentTransaction.asset_id = value;
    if (value !== null && value !== "") {
      delete newErrors.asset_id;
    }
    const selectedAsset = (availableAssets[index] || []).find(
      (row) => String(row.id) === String(value)
    );
    if (isExemptFromAssetDateConflict(selectedAsset)) {
      delete newErrors.asset_id;
    }
  }

  // Handle date field changes — keep the mapped asset; availability is re-checked
  // after the debounced asset fetch below.
  const isDateField = name === "start_date" || name === "end_date";

  // ✅ Unit validation
  if (name === "unit") {
    currentTransaction.unit = value || null;
    if (!value) {
      newErrors.unit = "Unit is required.";
    } else {
      delete newErrors.unit;
    }
  }

  // ✅ Rate validation
  if (name === "rate") {
    const parsedValue = value === "" ? null : parseFloat(value);
    if (parsedValue === null || parsedValue < 0 || isNaN(parsedValue)) {
      newErrors.rate = "Rate is required and must be non-negative.";
    } else {
      delete newErrors.rate;
    }
    currentTransaction.rate = parsedValue;
  }

  // ✅ Tax Type validation + default GST %
  if (name === "tax_type") {
    currentTransaction.tax_type = value || null;
    delete newErrors.tax_type;
    // Default common GST rates when user picks a type (editable afterward)
    if (value === "CGST-SGST" || value === "IGST") {
      if (
        currentTransaction.tax_percentage == null ||
        currentTransaction.tax_percentage === "" ||
        Number(currentTransaction.tax_percentage) === 0
      ) {
        currentTransaction.tax_percentage = 18;
      }
    }
  }

  // ✅ Tax Percentage validation
  if (name === "tax_percentage") {
    const parsedValue = value === "" ? null : parseFloat(value);
    if (parsedValue !== null && (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100)) {
      newErrors.tax_percentage = "Must be between 0 and 100.";
    } else {
      delete newErrors.tax_percentage;
    }
    currentTransaction.tax_percentage = parsedValue;
  }

  // Date validation
  const masterStartDateOnly =
    masterFormData.wo_start ?? masterWorkOrderDates.wo_start ?? null;
  const masterEndDateOnly =
    masterFormData.wo_end ?? masterWorkOrderDates.wo_end ?? null;

  const tranStartDate = currentTransaction.start_date 
    ? new Date(currentTransaction.start_date + 'T00:00:00') 
    : null;
  const tranEndDate = currentTransaction.end_date 
    ? new Date(currentTransaction.end_date + 'T23:59:59') 
    : null;

  const masterStart = masterStartDateOnly 
    ? new Date(masterStartDateOnly + 'T00:00:00') 
    : null;
  const masterEnd = masterEndDateOnly 
    ? new Date(masterEndDateOnly + 'T23:59:59') 
    : null;

  if (tranStartDate && masterStart && tranStartDate < masterStart) {
    newErrors.start_date = "Must be after Work Order Master Start Date.";
  } else if (tranStartDate && masterEnd && tranStartDate > masterEnd) {
    newErrors.start_date = "Must be before Work Order Master End Date.";
  } else {
    delete newErrors.start_date;
  }

  if (tranEndDate && masterStart && tranEndDate < masterStart) {
    newErrors.end_date = "Must be after Work Order Master Start Date.";
  } else if (tranEndDate && masterEnd && tranEndDate > masterEnd) {
    newErrors.end_date = "Must be before Work Order Master End Date.";
  } else if (tranStartDate && tranEndDate && tranEndDate < tranStartDate) {
    newErrors.end_date = "Must be after Transaction Start Date.";
  } else {
    delete newErrors.end_date;
  }

  if (
    isDateField &&
    tranStartDate &&
    tranEndDate &&
    !newErrors.start_date &&
    !newErrors.end_date
  ) {
    const debouncedFetch = debounce(fetchAssetsForTransaction, 800);
    debouncedFetch(
      index,
      currentTransaction.start_date,
      currentTransaction.end_date,
      {
        excludeTranId: currentTransaction.dbId,
        preserveAssetId: currentTransaction.asset_id,
        checkConflict: true,
      }
    );
  } else if (isDateField && (!tranStartDate || !tranEndDate)) {
    setAvailableAssets((prev) => ({ ...prev, [index]: [] }));
  }

  if (
    !currentTransaction.original_start_date &&
    tranStartDate &&
    !newErrors.start_date
  ) {
    currentTransaction.original_start_date = currentTransaction.start_date;
  }
  if (
    !currentTransaction.original_end_date &&
    tranEndDate &&
    !newErrors.end_date
  ) {
    currentTransaction.original_end_date = currentTransaction.end_date;
  }

  // ✅ RECALCULATE when these fields change
const fieldsAffectingCalculation = [
  'quantity',
  'rate',
  'mob',
  'demob',
  'tax_percentage',
  'tax_type',
  'unit',
  'working_hrs',
  'monthly_working_days'
];

  if (fieldsAffectingCalculation.includes(name)) {
  // Pass the current transaction to calculate amounts
  const calculatedAmounts = calculateTransactionAmounts(currentTransaction);

  // Update hidden fields
  currentTransaction.rate_per_hr = calculatedAmounts.rate_per_hr;
  currentTransaction.daily_rate = calculatedAmounts.daily_rate;
  currentTransaction.monthly_rate = calculatedAmounts.monthly_rate;
  
  // Update visible calculated fields
  currentTransaction.taxable_amt = calculatedAmounts.taxable_amt;
  currentTransaction.tax_amount = calculatedAmounts.tax_amount;
  currentTransaction.total_amount = calculatedAmounts.total_amount;
  
  // Keep old 'amount' field for backwards compatibility
  currentTransaction.amount = calculatedAmounts.taxable_amt;
  
  console.log(`✅ Amounts recalculated for field '${name}':`, {
    rate_per_hr: calculatedAmounts.rate_per_hr,
    daily_rate: calculatedAmounts.daily_rate,
    monthly_rate: calculatedAmounts.monthly_rate,
    taxable_amt: calculatedAmounts.taxable_amt,
    tax_amount: calculatedAmounts.tax_amount,
    total_amount: calculatedAmounts.total_amount
  });

  // Recalculate aggregate totals
  const updatedTxns = [...updatedTransactions];
  updatedTxns[index] = currentTransaction;
  const newAggregates = calculateAggregateTotals(updatedTxns);
  setAggregateTotals(newAggregates);
}

  // Handle main_equipment
  if (name === "main_equipment" && checked) {
    updatedTransactions.forEach((tran, i) => {
      if (i !== index) {
        tran.main_equipment = false;
      }
    });
  }

  // Check for duplicate assets
  if (name === "asset_id_combobox" || name === "asset_id") {
    const isDuplicate = updatedTransactions.some(
      (t, i) =>
        i !== index &&
        t.asset_id !== null &&
        t.asset_id === currentTransaction.asset_id
    );
    if (currentTransaction.asset_id !== null && isDuplicate) {
      newErrors.asset_id = "This asset is already used in another transaction.";
    } else {
      delete newErrors.asset_id;
    }
  }

  // Handle hsd_scope
  if (name === "hsd_scope") {
    if (value === null || value === "") {
      newErrors.hsd_scope = "HSD Scope is required.";
    } else {
      delete newErrors.hsd_scope;
    }
  }

  updatedTransactions[index] = currentTransaction;
  setTransactions(updatedTransactions);
  setTransactionErrors((prevErrors) => ({
    ...prevErrors,
    [index]: newErrors,
  }));
};

  const saveAllTransactions = async () => {
    setIsLoadingTransactions(true);
    let allSuccess = true;
    const errorsDuringSave = {};

    for (let i = 0; i < transactions.length; i++) {
      const tran = transactions[i];
      const result = await saveSingleTransaction(tran, i);
      if (!result.success) {
        allSuccess = false;
        errorsDuringSave[i] = {
          ...errorsDuringSave[i],
          ...result.errors,
          general: result.message,
        };
      }
    }
    setTransactionErrors((prev) => ({ ...prev, ...errorsDuringSave }));
    setIsLoadingTransactions(false);

    if (allSuccess) {
      showCustomAlert("All transactions saved successfully!");
      
      // Calculate aggregate totals
      const aggregates = calculateAggregateTotals(transactions);
      setAggregateTotals(aggregates);
      
      setMasterFormData((prevData) => ({
        ...prevData,
        TaxableAmt: aggregates.totalTaxableAmount,
        tot_value: aggregates.totalAmount,
      }));
      await handleMasterUpdate(null, { 
        TaxableAmt: aggregates.totalTaxableAmount,
        tot_value: aggregates.totalAmount 
      });
      navigate("/work-orders");
    } else {
      showCustomAlert(
        "Some transactions failed to save. Please correct the highlighted rows."
      );
    }
  };

  if (isFormLoading && propWorkOrderId) {
    return (
      <div className="work-order-form-container loading-message">
        Loading Work Order Data...
      </div>
    );
  }

  console.log(masterDropdowns);

  return (
    <>
      <div className="work-order-form-container">
        <h2>
          {masterWorkOrderId ? "Edit Work Order" : "Create New Work Order"}
        </h2>
        <form
          onSubmit={masterWorkOrderId ? handleMasterUpdate : handleMasterSubmit}
          className="work-order-form"
        >
          {masterFormErrors.error && (
            <p className="form-general-error">{masterFormErrors.error}</p>
          )}

          {/* First Row: Created Date and Doc No */}
          <div className="form-row summary-row top-row">
            <div className="form-group summary-item">
              <label>Created Date:</label>
              <span className="summary-value">
                {masterFormData.add_date 
                  ? formatDateTimeToDDMMYYYY(masterFormData.add_date)
                  : "N/A"}
              </span>
            </div>
            <div className="form-group summary-item">
              <label>Doc No.:</label>
              <span className="summary-value">{masterFormData.doc_no || "N/A"}</span>
            </div>
          </div>

          {/* Second Row: All Amount Details in ONE line */}
          <div className="form-row summary-row amounts-row">
            <div className="form-group summary-item">
              <label>Taxable Amount:</label>
              <span className="summary-value highlight-taxable">
                ₹{aggregateTotals.totalTaxableAmount || "0.00"}
              </span>
            </div>
            <div className="form-group summary-item">
              <label>Tax Amount:</label>
              <span className="summary-value highlight-tax">
                ₹{aggregateTotals.totalTaxAmount || "0.00"}
              </span>
            </div>
            <div className="form-group summary-item">
              <label>Total Amount:</label>
              <span className="summary-value highlight-total">
                ₹{aggregateTotals.totalAmount || "0.00"}
              </span>
            </div>
            <div className="form-group summary-item">
              <label>Monthly Hours:</label>
              <span className="summary-value">{masterFormData.monthly_hrs || "N/A"}</span>
            </div>
            <div className="form-group summary-item">
              <label>Total Assets:</label>
              <span className="summary-value">{transactions.length || 0}</span>
            </div>
          </div>

          <div className="form-row four-column-row">
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("wo_no") ? "has-error" : ""
              }`}
            >
              <label htmlFor="wo_no">
                Work Order No.:<span className="required">*</span>
              </label>
              <input
                type="text"
                id="wo_no"
                name="wo_no"
                value={masterFormData.wo_no || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("wo_no") && (
                <p className="error-message">Work Order No. is required.</p>
              )}
              {/* REMOVED: Inline duplicate warning - will only show on submit via window.confirm */}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("wo_date") ? "has-error" : ""
              }`}
            >
              <label htmlFor="wo_date">
                Work Order Date:<span className="required">*</span>
              </label>
              <input
                type="date"
                id="wo_date"
                name="wo_date"
                value={masterFormData.wo_date || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("wo_date") && (
                <p className="error-message">Work Order Date is required.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("wo_start") ? "has-error" : ""
              }`}
            >
              <label htmlFor="wo_start">
                Work Order Start:<span className="required">*</span>
              </label>
              <input
                type="date"
                id="wo_start"
                name="wo_start"
                value={masterFormData.wo_start || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("wo_start") && (
                <p className="error-message">
                  Work Order Start is required or has conflicts.
                </p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("wo_end") ? "has-error" : ""
              }`}
            >
              <label htmlFor="wo_end">
                Work Order End:<span className="required">*</span>
              </label>
              <input
                type="date"
                id="wo_end"
                name="wo_end"
                value={masterFormData.wo_end || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("wo_end") && (
                <p className="error-message">
                  Work Order End is required or has conflicts.
                </p>
              )}
            </div>
          </div>

          <div className="form-row four-column-row">
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("cust_id") ? "has-error" : ""
              }`}
            >
              <label htmlFor="cust_id">
                Customer:<span className="required">*</span>
              </label>
              <select
                name="cust_id"
                id="cust_id"
                value={masterFormData.cust_id || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              >
                <option value="">Select Customer</option>
                {masterDropdowns?.customers?.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.value}
                  </option>
                ))}
              </select>
              {masterFormErrors.fields.includes("cust_id") && (
                <p className="error-message">Customer is required.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("project_id")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="project_id">
                Project:<span className="required">*</span>
              </label>
              <select
                name="project_id"
                id="project_id"
                value={masterFormData.project_id || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              >
                <option value="">Select Project</option>
                {masterDropdowns?.projects?.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.value}
                  </option>
                ))}
              </select>
              {masterFormErrors.fields.includes("project_id") && (
                <p className="error-message">Project is required.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("amendment_no")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="amendment_no">Amendment No.:</label>
              <input
                type="text"
                id="amendment_no"
                name="amendment_no"
                value={masterFormData.amendment_no || ""}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("amendment_no") && (
                <p className="error-message">Amendment No. has an error.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("amendment_date")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="amendment_date">Amendment Date:</label>
              <input
                type="date"
                id="amendment_date"
                name="amendment_date"
                value={masterFormData.amendment_date || ""}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("amendment_date") && (
                <p className="error-message">Amendment Date has an error.</p>
              )}
            </div>
          </div>

          <div className="form-row four-column-row">
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("no_shifts") ? "has-error" : ""
              }`}
            >
              <label htmlFor="no_shifts">
                Number of Shifts:<span className="required">*</span>
              </label>
              <select
                id="no_shifts"
                name="no_shifts"
                value={masterFormData.no_shifts || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              >
                <option value="">Select Number of Shifts</option>
                <option value="1">1</option>
                <option value="2">2</option>
              </select>
              {masterFormErrors.fields.includes("no_shifts") && (
                <p className="error-message">Number of Shifts is required.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("shift_hours")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="shift_hours">Shift Hours:</label>
              <input
                type="number"
                id="shift_hours"
                name="shift_hours"
                value={masterFormData.shift_hours || ""}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("shift_hours") && (
                <p className="error-message">Shift Hours has an error.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("demob_nil_after")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="demob_nil_after">Demob Nil After (Months):</label>
              <input
                type="number"
                id="demob_nil_after"
                name="demob_nil_after"
                value={masterFormData.demob_nil_after || ""}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("demob_nil_after") && (
                <p className="error-message">Demob Nil After has an error.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("sector") ? "has-error" : ""
              }`}
            >
              <label htmlFor="sector">
                Sector:<span className="required">*</span>
              </label>
              <select
                name="sector"
                id="sector"
                value={masterFormData.sector || ""}
                onChange={handleMasterChange}
                required
                disabled={isLoadingMaster}
              >
                <option value="">Select Sector</option>
                {masterDropdowns?.sectors?.map((sector) => (
                  <option key={sector.id} value={sector.id}>
                    {sector.value}
                  </option>
                ))}
              </select>
              {masterFormErrors.fields.includes("sector") && (
                <p className="error-message">Sector is required.</p>
              )}
            </div>
          </div>

          <div className="form-row">
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("weekly_off")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="weekly_off">
                Weekly Off:
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "4px",
                  }}
                >
                  <input
                    type="checkbox"
                    id="include_woff"
                    name="include_woff"
                    checked={masterFormData.include_woff}
                    onChange={handleMasterChange}
                    disabled={isLoadingMaster}
                  />
                  <label htmlFor="include_woff" style={{ margin: 0 }}>
                    Include Weekly Off
                  </label>
                </div>
              </label>
              <select
                name="weekly_off"
                id="weekly_off"
                value={masterFormData.weekly_off || ""}
                onChange={handleMasterChange}
                disabled={!masterFormData.include_woff || isLoadingMaster}
              >
                <option value="">Select Day</option>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (value, index) => (
                    <option key={index} value={value}>
                      {value}
                    </option>
                  )
                )}
              </select>
              {masterFormErrors.fields.includes("weekly_off") && (
                <p className="error-message">Weekly Off has an error.</p>
              )}
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("kind_attn") ? "has-error" : ""
              }`}
            >
              <label htmlFor="kind_attn">Kind Attention:</label>
              <input
                type="text"
                id="kind_attn"
                name="kind_attn"
                value={masterFormData.kind_attn || ""}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
              />
              {masterFormErrors.fields.includes("kind_attn") && (
                <p className="error-message">Kind Attention has an error.</p>
              )}
            </div>
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="lunch"
                name="lunch"
                checked={masterFormData.lunch}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
              />
              <label htmlFor="lunch">Lunch Included</label>
            </div>
          </div>
          <div className="form-row">
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("remarks") ? "has-error" : ""
              }`}
            >
              <label htmlFor="remarks">Remarks:</label>
              <textarea
                id="remarks"
                name="remarks"
                value={masterFormData.remarks || ""}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
                rows={1}
              ></textarea>
              {masterFormErrors.fields.includes("remarks") && (
                <p className="error-message">Remarks has an error.</p>
              )}
            </div>
          </div>
          <div className="form-row">
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("attachment_url")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="attachment_url">Attachments:</label>
              <input
                type="file"
                value={
                  !(Array.isArray(masterRecordFiles["attachment_url"]) &&
                    masterRecordFiles["attachment_url"].length)
                    ? ""
                    : undefined
                }
                id="attachment_url"
                name="attachment_url"
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
                multiple={true}
              />
              <div className="file-list">
                {(Array.isArray(masterRecordFiles["attachment_url"])
                  ? masterRecordFiles["attachment_url"]
                  : []
                ).map((file, i) => (
                  <div key={file.id || i} className="file-item">
                    {file.name ||
                      (file.file_url
                        ? String(file.file_url).split("/").pop()
                        : `File ${i + 1}`)}
                    <button
                      type="button"
                      onClick={async () => {
                        const currentFiles = Array.isArray(
                          masterRecordFiles["attachment_url"]
                        )
                          ? masterRecordFiles["attachment_url"]
                          : [];
                        if (propWorkOrderId && file.id) {
                          const deleteFileResponse = await fetch(
                            `${getNodeServiceBaseUrl()}/attachment/api/attachments/${file.id}`,
                            {
                              method: "DELETE",
                              headers: {
                                Authorization: `Bearer ${token}`,
                              },
                            }
                          );
                          if (!deleteFileResponse.ok) {
                            showCustomAlert("Not able to delete file.");
                            return;
                          }
                        }
                        setMasterRecordFiles({
                          attachment_url: currentFiles.filter((f) => f != file),
                        });
                      }}
                    >
                      <IoTrash height={"1rem"} width={"1rem"} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div
              className={`form-group ${
                masterFormErrors.fields.includes("attachment_remarks")
                  ? "has-error"
                  : ""
              }`}
            >
              <label htmlFor="attachment_remarks">Accessories Remarks:</label>
              <textarea
                id="attachment_remarks"
                name="attachment_remarks"
                value={masterFormData.attachment_remarks || ""}
                onChange={handleMasterChange}
                disabled={isLoadingMaster}
                rows={1}
              ></textarea>
              {masterFormErrors.fields.includes("attachment_remarks") && (
                <p className="error-message">Remarks has an error.</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="submit-button"
            disabled={isLoadingMaster}
          >
            {isLoadingMaster
              ? masterWorkOrderId
                ? "Updating Work Order..."
                : "Creating Work Order..."
              : masterWorkOrderId
              ? "Update Work Order"
              : "Create Work Order"}
          </button>
        </form>

        {masterWorkOrderId && (
          <div className="transaction-section">
            <h3>Work Order Transactions for WO ID: {masterWorkOrderId}</h3>
            <p className="master-dates-info">
  Master WO Period:{" "}
  {formatDateToDDMMYYYY(masterFormData.wo_start ?? masterWorkOrderDates.wo_start) || "N/A"} to{" "}
  {formatDateToDDMMYYYY(masterFormData.wo_end ?? masterWorkOrderDates.wo_end) || "N/A"}
</p>
            <div className="transaction-table-container">
              {transactions.forEach((_, i) => {
                const currentErrors = transactionErrors[i];
                console.log(currentErrors);
                return currentErrors?.general;
              })}
              <table>
                <thead>
                  <tr>
                    <th>SR No.</th>
                    <th>
                      Start Date<span className="required">*</span>
                    </th>
                    <th>
                      End date<span className="required">*</span>
                    </th>
                    <th style={{ minWidth: "200px" }}>
                      Asset<span className="required">*</span>
                    </th>
                    <th style={{ display: "none" }}>Rate/Hr</th>
                    <th style={{ display: "none" }}>Daily rate</th>
                    <th style={{ display: "none" }}>Monthly rate</th>
                    <th style={{ minWidth: "120px" }}>
                      Monthly Working Days<span className="required">*</span>
                    </th>
                    <th style={{ minWidth: "100px" }}>
                      Quantity<span className="required">*</span>
                    </th>
                    <th style={{ minWidth: "150px" }}>
                      Unit<span className="required">*</span>
                    </th>
                    <th style={{ minWidth: "120px" }}>
                      Rate<span className="required">*</span>
                    </th>
                    <th style={{ minWidth: "120px" }}>MOB</th>
                    <th style={{ minWidth: "120px" }}>DEMOB</th>
                    <th style={{ minWidth: "100px" }}>
                      Working Hrs (Max 24)<span className="required">*</span>
                    </th>
                    <th style={{ minWidth: "150px" }}>Taxable Amount</th>
                    <th style={{ minWidth: "150px" }}>Tax Type</th>
                    <th style={{ minWidth: "120px" }}>Tax %</th>
                    <th style={{ minWidth: "150px" }}>Tax Amount</th>
                    <th style={{ minWidth: "150px" }}>Total Amount</th>
                    <th style={{ minWidth: "120px" }}>Breakdown penalty</th>
                    <th style={{ minWidth: "120px" }}>Penalty unit</th>
                    <th style={{ minWidth: "120px" }}>
                      Main Equipment
                    </th>
                    <th style={{ minWidth: "120px" }}>
                      HSD Scope<span className="required">*</span>
                    </th>
                    <th style={{ minWidth: "80px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 && (
                    <tr>
                      <td colSpan="24" style={{ textAlign: "center" }}>
                        No transactions added yet.
                      </td>
                    </tr>
                  )}
                  {transactions?.map((tran, index) => {
                    const currentErrors = transactionErrors[index] || {};
                    const hasDateError =
                      currentErrors.start_date || currentErrors.end_date;

                    return (
                      <tr
                        key={tran.id}
                        className={
                          Object.keys(currentErrors).length > 0
                            ? "has-error"
                            : ""
                        }
                      >
                        <td>{tran.wo_sr_no}</td>
                        <td>
                          <input
                            type="date"
                            name="start_date"
                            value={tran.start_date || ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            className={
                              currentErrors.start_date ? "input-error" : ""
                            }
                          />
                          {currentErrors.start_date && (
                            <p className="error-message-inline">
                              {currentErrors.start_date}
                            </p>
                          )}
                        </td>
                        <td>
                          <input
                            type="date"
                            name="end_date"
                            value={tran.end_date || ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            className={
                              currentErrors.end_date ? "input-error" : ""
                            }
                          />
                          {currentErrors.end_date && (
                            <p className="error-message-inline">
                              {currentErrors.end_date}
                            </p>
                          )}
                        </td>
                        <td>
                          <SearchableDropdown
                            options={availableAssets[index] || []}
                            selectedValues={tran.asset_id}
                            onChange={(selectedAssetId) =>
                              handleChangeTransaction(index, {
                                target: {
                                  name: "asset_id_combobox",
                                  value: selectedAssetId,
                                },
                              })
                            }
                            placeholder={
                              hasDateError ? "Set dates first" : "Select Asset"
                            }
                            labelField={["name", "serial_number", "asset_code", "registration_number"]}
                            valueField="id"
                            disabled={
                              isLoadingTransactions ||
                              hasDateError ||
                              !tran.start_date ||
                              !tran.end_date
                            }
                            error={currentErrors.asset_id}
                          />
                          {currentErrors.asset_id && (
                            <p className="error-message-inline">
                              {currentErrors.asset_id}
                            </p>
                          )}
                        </td>
                        <td style={{ display: "none" }}>
                          <input
                            type="number"
                            name="rate_per_hr"
                            value={tran.rate_per_hr || ""}
                            readOnly
                            className="readonly-input"
                          />
                        </td>
                        <td style={{ display: "none" }}>
                          <input
                            type="number"
                            name="daily_rate"
                            value={tran.daily_rate || ""}
                            readOnly
                            className="readonly-input"
                          />
                        </td>
                        <td style={{ display: "none" }}>
                          <input
                            type="number"
                            name="monthly_rate"
                            value={tran.monthly_rate || ""}
                            readOnly
                            className="readonly-input"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            name="monthly_working_days"
                            value={tran.monthly_working_days !== null ? tran.monthly_working_days : ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            placeholder="Working Days"
                            min="1"
                            max="31"
                            className={currentErrors.monthly_working_days ? "input-error" : ""}
                          />
                          {currentErrors.monthly_working_days && (
                            <p className="error-message-inline">
                              {currentErrors.monthly_working_days}
                            </p>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            name="quantity"
                            value={tran.quantity !== null && tran.quantity !== "" ? tran.quantity : ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            placeholder="Quantity"
                            step="1" // Changed to step="1" for integer
                            min="1"
                            className={currentErrors.quantity ? "input-error" : ""}
                          />
                          {currentErrors.quantity && (
                            <p className="error-message-inline">
                              {currentErrors.quantity}
                            </p>
                          )}
                        </td>
                        <td>
                          <select
                            name="unit"
                            value={tran.unit || ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            className={currentErrors.unit ? "input-error" : ""}
                          >
                            <option value="">Select Unit</option>
                            {transactionDropdowns.unitOptions?.map((unit) => (
                              <option key={unit.id} value={unit.id}>
                                {unit.value}
                              </option>
                            ))}
                          </select>
                          {currentErrors.unit && (
                            <p className="error-message-inline">{currentErrors.unit}</p>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            name="rate"
                            value={tran.rate !== null && tran.rate !== "" ? tran.rate : ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            placeholder="Rate"
                            step="0.01"
                            min="0"
                            className={currentErrors.rate ? "input-error" : ""}
                          />
                          {currentErrors.rate && (
                            <p className="error-message-inline">{currentErrors.rate}</p>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            name="mob"
                            value={tran.mob !== null ? tran.mob : ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            placeholder="MOB"
                            step="0.01"
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            name="demob"
                            value={tran.demob !== null ? tran.demob : ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            placeholder="DEMOB"
                            step="0.01"
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            name="working_hrs"
                            value={
                              tran.working_hrs !== null ? tran.working_hrs : ""
                            }
                            onChange={(e) => handleChangeTransaction(index, e)}
                            className={
                              currentErrors.working_hrs ? "input-error" : ""
                            }
                          />
                          {currentErrors.working_hrs && (
                            <p className="error-message-inline">
                              {currentErrors.working_hrs}
                            </p>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            name="taxable_amt"
                            value={tran.taxable_amt || ""}
                            readOnly
                            className="readonly-input"
                            style={{ backgroundColor: "#f0f0f0" }}
                          />
                        </td>
                        <td>
                          <select
                            name="tax_type"
                            value={tran.tax_type || ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            className={currentErrors.tax_type ? "input-error" : ""}
                          >
                            <option value="">Select Tax Type</option>
                            {transactionDropdowns.taxTypes?.map((taxType) => (
                              <option key={taxType.id} value={taxType.value}>
                                {taxType.value}
                              </option>
                            ))}
                          </select>
                          {currentErrors.tax_type && (
                            <p className="error-message-inline">{currentErrors.tax_type}</p>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            name="tax_percentage"
                            value={tran.tax_percentage !== null && tran.tax_percentage !== "" ? tran.tax_percentage : ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            placeholder="Tax %"
                            step="0.01"
                            min="0"
                            max="100"
                            className={currentErrors.tax_percentage ? "input-error" : ""}
                          />
                          {currentErrors.tax_percentage && (
                            <p className="error-message-inline">{currentErrors.tax_percentage}</p>
                          )}
                        </td>
                        <td>
                          <input
                            type="number"
                            name="tax_amount"
                            value={tran.tax_amount || ""}
                            readOnly
                            className="readonly-input"
                            style={{ backgroundColor: "#f0f0f0" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            name="total_amount"
                            value={tran.total_amount || ""}
                            readOnly
                            className="readonly-input"
                            style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            name="breakdown_penalty"
                            value={
                              tran.breakdown_penalty !== null
                                ? tran.breakdown_penalty
                                : ""
                            }
                            onChange={(e) => handleChangeTransaction(index, e)}
                          />
                        </td>
                        <td>
                          <select
                            name="breakdown_penalty_unit"
                            value={tran.breakdown_penalty_unit || ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                          >
                            <option value="">Select Unit</option>
                            {transactionDropdowns.breakdownUnits?.map(
                              (unit) => (
                                <option key={unit.id} value={unit.id}>
                                  {unit.value}
                                </option>
                              )
                            )}
                          </select>
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            name="main_equipment"
                            checked={tran.main_equipment}
                            onChange={(e) => handleChangeTransaction(index, e)}
                          />
                        </td>
                        <td>
                          <select
                            name="hsd_scope"
                            value={tran.hsd_scope || ""}
                            onChange={(e) => handleChangeTransaction(index, e)}
                            className={
                              currentErrors.hsd_scope ? "input-error" : ""
                            }
                          >
                            <option value="">Select Scope</option>
                            <option value="Customer">Customer</option>
                            <option value="Owner">PLS</option>
                          </select>
                          {currentErrors.hsd_scope && (
                            <p className="error-message-inline">
                              {currentErrors.hsd_scope}
                            </p>
                          )}
                        </td>
                        <td>
                          <div className="transaction-row-actions">
                            <button
                              type="button"
                              onClick={() => resetTransactionDates(index)}
                              className="reset-transaction-button"
                              disabled={
                                isLoadingTransactions ||
                                !tran.original_start_date ||
                                !tran.original_end_date ||
                                (tran.start_date === tran.original_start_date &&
                                  tran.end_date === tran.original_end_date)
                              }
                              title="Reset Dates"
                              aria-label="Reset Dates"
                            >
                              <IoRefresh size={16} aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                removeTransaction(index, tran.dbId)
                              }
                              className="remove-transaction-button"
                              title="Remove"
                              aria-label="Remove"
                            >
                              <IoTrash size={16} aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="transaction-actions">
              <button
                type="button"
                onClick={addTransaction}
                className="add-transaction-button"
                disabled={isLoadingTransactions}
              >
                {isLoadingTransactions ? "Processing..." : "Add Transaction"}
              </button>
              <button
                type="button"
                onClick={saveAllTransactions}
                className="submit-button"
                disabled={isLoadingTransactions || transactions.length === 0}
              >
                {isLoadingTransactions
                  ? "Saving Transactions..."
                  : "Save All Transactions"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default WorkOrderForm;